////////////////////////////////////////////////////////////
//                                                          //
//  Program: CreditorForm.jsx                              //
//  Application: Misc                                      //
//  Option: Add a Creditor / view a Creditor's profile      //
//          plus Stripe onboarding invite (FF&E and Intro)  //
//                                                          //
////////////////////////////////////////////////////////////

// Add mode (no :slug in the route) only collects Name, Email, Phone, then
// saves and redirects straight into view mode for the new creditor — per
// the client's requested flow. View mode (/creditors/view/:slug) shows the
// same fields (now editable, including Status) plus a Stripe onboarding
// section with a "Send Invite" action per fee type (FF&E, Introductory
// Brokerage Fee), reusing the same backend call CreditorStripeSetup.jsx
// uses so the creditor gets the exact same onboarding email either way.

import React, { Component } from "react";
import { Container, Form } from "react-bootstrap";
import Moment from "moment";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import Alerts from "../common/Alerts";
import CreditorService from "../../services/CreditorService";
import WHPayoutService from "../../services/WHPayoutService";

const FEE_TYPES = [
  { key: "ffe", label: "FF&E Brokerage Fee" },
  { key: "intro", label: "Introductory Brokerage Fee" },
];

// Same cadence options CreditorStripeSetup.jsx offers — this view now owns
// setting cadence too, so a creditor's own payout cadence is editable from
// right here on their profile instead of only from the Master Data setup list.
const CADENCE_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

export class CreditorForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      slug: this.props.match.params.slug,
      creditor_id: "",
      name: "",
      email: "",
      phone: "",
      status_id: true,
      isSubmit: false,
      showSpinner: false,
      loading: false,
      isSend: false,
      // Which fee's invite button is currently in flight — isSend alone was
      // shared across both rows, so clicking one button showed the loading
      // state (and disabled) on both rows, not just the one clicked.
      sendingFeeKey: null,
      confirm_back: 0,
      ffe_stripe_status: "",
      intro_stripe_status: "",
      ffe_invite_sent_at: null,
      intro_invite_sent_at: null,
      ffe_payout_cadence: "monthly",
      intro_payout_cadence: "monthly",
      isCadenceSaving: false,
      errors: {},
      showAlertModal: false,
      alertModalType: "",
      alertModalTitle: "",
      alertModalMessage: "",
    };

    this.changeHandler = this.changeHandler.bind(this);
  }

  componentDidMount() {
    if (this.state.slug) {
      this.getCreditorData();
    }
  }

  async getCreditorData() {
    this.setState({ showSpinner: true, loading: true });
    let res = await CreditorService.getCreditor(this.state.slug);
    if (global.successStatus.includes(res.status) && res.data) {
      this.setState(
        {
          creditor_id: res.data.creditor_id,
          name: res.data.name || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          status_id: res.data.status_id === 1,
        },
        // creditor_id must be set before matching against the Stripe status list below.
        () => this.getStripeStatus(),
      );
    } else if (res.status === 403) {
      // A Creditor-role user landed on someone else's creditor URL (typed
      // directly or an old bookmark) — bounce them to their own record with
      // no error shown, rather than revealing that another creditor exists.
      let ownSlug = localStorage.getItem("creditorSlug");
      this.props.history.push(
        ownSlug ? "/creditors/view/" + ownSlug : "/creditors",
      );
    }
    this.setState({ showSpinner: false, loading: false });
  }

  // Reuses the same list endpoint CreditorStripeSetup.jsx reads from, so the
  // status shown here always matches what that screen shows.
  async getStripeStatus() {
    let res = await WHPayoutService.getWHPayoutReport("");
    if (global.successStatus.includes(res.status)) {
      let creditors = res.data && res.data.creditors ? res.data.creditors : [];
      let match = creditors.find(
        (c) => String(c.creditor_id) === String(this.state.creditor_id),
      );
      if (match) {
        this.setState({
          ffe_stripe_status: match.ffe_stripe_status || "",
          intro_stripe_status: match.intro_stripe_status || "",
          ffe_invite_sent_at: match.ffe_invite_sent_at || null,
          intro_invite_sent_at: match.intro_invite_sent_at || null,
          ffe_payout_cadence: match.ffe_payout_cadence || "monthly",
          intro_payout_cadence: match.intro_payout_cadence || "monthly",
        });
      }
    }
  }

  changeHandler = (event) => {
    let value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    this.setState({ [event.target.name]: value, confirm_back: 1 });

    if (event.target.name === "name" && this.state.errors.name) {
      let errors = this.state.errors;
      delete errors.name;
      this.setState({ errors: errors });
    }
  };

  cancelClick = () => {
    if (this.state.confirm_back === 1) {
      if (window.confirm("Do you want to exit without saving?")) {
        this.props.history.push("/creditors");
      }
    } else {
      this.props.history.push("/creditors");
    }
  };

  validate() {
    let errors = {};
    if (!this.state.name || !this.state.name.trim()) {
      errors.name = "Name is required.";
    }
    // Email is required on the backend now — a login is auto-provisioned for
    // every creditor, and that can't happen without an email address.
    if (!this.state.email || !this.state.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.email)) {
      errors.email = "Enter a valid email.";
    }
    this.setState({ errors: errors });
    return Object.keys(errors).length === 0;
  }

  saveCreditor = async (e) => {
    e.preventDefault();
    if (!this.validate()) return;

    this.setState({ showSpinner: true, isSubmit: true });
    let res = await CreditorService.createCreditor({
      name: this.state.name,
      email: this.state.email,
      phone: this.state.phone,
    });
    if (
      global.successStatus.includes(res.status) &&
      res.data &&
      res.data.slug
    ) {
      this.props.history.push("/creditors");
    } else {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: res.data ? res.data.errorCode || "Error!" : "Error!",
        alertModalMessage: res.data
          ? res.data.errorMessage || res.data.message
          : "Error!",
      });
    }
    this.setState({ showSpinner: false, isSubmit: false });
    setTimeout(
      () => this.setState({ showAlertModal: false }),
      global.alert_close_time,
    );
  };

  updateCreditor = async (e) => {
    e.preventDefault();
    if (!this.validate()) return;

    this.setState({ showSpinner: true, isSubmit: true });
    let res = await CreditorService.updateCreditor({
      slug: this.state.slug,
      name: this.state.name,
      email: this.state.email,
      phone: this.state.phone,
      status_id: this.state.status_id ? 1 : 2,
    });
    if (global.successStatus.includes(res.status)) {
      this.setState({
        confirm_back: 0,
        showAlertModal: true,
        alertModalType: "success",
        alertModalTitle: "Success",
        alertModalMessage: res.message || "Success",
      });
    } else {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: res.data ? res.data.errorCode || "Error!" : "Error!",
        alertModalMessage: res.data
          ? res.data.errorMessage || res.data.message
          : "Error!",
      });
    }
    this.setState({ showSpinner: false, isSubmit: false });
    setTimeout(
      () => this.setState({ showAlertModal: false }),
      global.alert_close_time,
    );
  };

  // Same backend call CreditorStripeSetup.jsx uses — creates/refreshes the
  // Stripe Express onboarding link for this one fee type and, on the backend
  // side, emails the creditor their onboarding link automatically. Safe to
  // call again once already connected — the backend simply skips generating
  // a new link/email in that case.
  sendStripeInvite = async (feeType) => {
    this.setState({ isSend: true, sendingFeeKey: feeType });
    let res = await WHPayoutService.createWHStripeExpress({
      creditor_id: this.state.creditor_id,
      fee_type: feeType,
      // So Stripe redirects back to this Creditor page after onboarding,
      // instead of the fixed WH Payout Report page.
      redirect_path: window.location.pathname,
    });
    if (global.successStatus.includes(res.status)) {
      let statusKey =
        feeType === "ffe" ? "ffe_stripe_status" : "intro_stripe_status";
      let sentAtKey =
        feeType === "ffe" ? "ffe_invite_sent_at" : "intro_invite_sent_at";
      let alreadyActive = this.rawStatus(feeType) === "active";
      this.setState({
        // The create endpoint responds with `<fee>_stripe_account_status`, a
        // different field name than the `<fee>_stripe_status` the list-status
        // endpoint uses — same mismatch CreditorStripeSetup.jsx already works
        // around by just setting "pending" locally rather than reading it back.
        // If already active/connected, the backend doesn't generate a new
        // link or send a new email, so neither the status nor the sent-at
        // timestamp should change here.
        [statusKey]: alreadyActive ? "active" : "pending",
        [sentAtKey]: alreadyActive
          ? this.inviteSentAt(feeType)
          : new Date().toISOString(),
        showAlertModal: true,
        alertModalType: "success",
        alertModalTitle: "Success",
        alertModalMessage: alreadyActive
          ? "This fee is already connected."
          : "Stripe onboarding invite sent.",
      });
    } else {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: res.data ? res.data.errorCode || "Error!" : "Error!",
        alertModalMessage: res.data
          ? res.data.errorMessage || res.data.message
          : "Failed to send invite.",
      });
    }
    this.setState({ isSend: false, sendingFeeKey: null });
    setTimeout(
      () => this.setState({ showAlertModal: false }),
      global.alert_close_time,
    );
  };

  // "Setup Directly" — same backend account/link creation as sendStripeInvite
  // above, but skip_invite_email tells the backend not to email the creditor
  // at all. Opens the onboarding link for the admin/concierge to complete
  // themselves, since there is no invite email for them to click through.
  sendStripeSetupDirect = async (feeType) => {
    this.setState({ isSend: true, sendingFeeKey: feeType + "_direct" });
    let res = await WHPayoutService.createWHStripeExpress({
      creditor_id: this.state.creditor_id,
      fee_type: feeType,
      skip_invite_email: 1,
      redirect_path: window.location.pathname,
    });
    if (global.successStatus.includes(res.status)) {
      let statusKey =
        feeType === "ffe" ? "ffe_stripe_status" : "intro_stripe_status";
      let alreadyActive = this.rawStatus(feeType) === "active";
      let onboardingUrl =
        res.data && res.data[feeType + "_stripe_onboarding_url"];
      this.setState({
        [statusKey]: alreadyActive ? "active" : "pending",
        showAlertModal: true,
        alertModalType: "success",
        alertModalTitle: "Success",
        alertModalMessage: alreadyActive
          ? "This fee is already connected."
          : "Opening Stripe setup — no invite email was sent.",
      });
      if (!alreadyActive && onboardingUrl) {
        window.open(onboardingUrl, "_blank", "noopener,noreferrer");
      }
    } else {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: res.data ? res.data.errorCode || "Error!" : "Error!",
        alertModalMessage: res.data
          ? res.data.errorMessage || res.data.message
          : "Failed to start Stripe setup.",
      });
    }
    this.setState({ isSend: false, sendingFeeKey: null });
    setTimeout(
      () => this.setState({ showAlertModal: false }),
      global.alert_close_time,
    );
  };

  // Same backend call CreditorStripeSetup.jsx uses — sets this one fee's
  // payout cadence (monthly/quarterly/annually) for this creditor.
  changeCadence = async (feeType, cadence) => {
    this.setState({ isCadenceSaving: true });
    let res = await WHPayoutService.updateWHStripeCadence({
      creditor_id: this.state.creditor_id,
      fee_type: feeType,
      cadence: cadence,
    });
    if (global.successStatus.includes(res.status)) {
      this.setState({
        ffe_payout_cadence: res.data.ffe_payout_cadence,
        intro_payout_cadence: res.data.intro_payout_cadence,
      });
    }
    this.setState({ isCadenceSaving: false });
  };

  rawStatus(feeKey) {
    return feeKey === "ffe"
      ? this.state.ffe_stripe_status
      : this.state.intro_stripe_status;
  }

  statusClass(feeKey) {
    let status = this.rawStatus(feeKey);
    if (status === "active") return "connected";
    if (status === "pending") return "awaiting";
    return "not-connected";
  }

  statusLabel(feeKey) {
    let cls = this.statusClass(feeKey);
    if (cls === "connected") return "Connected";
    if (cls === "awaiting") return "Awaiting setup";
    return "Not connected";
  }

  inviteSentAt(feeKey) {
    return feeKey === "ffe"
      ? this.state.ffe_invite_sent_at
      : this.state.intro_invite_sent_at;
  }

  metaText(feeKey) {
    let cls = this.statusClass(feeKey);
    if (cls === "connected") return "Payouts enabled";
    let sentAt = this.inviteSentAt(feeKey);
    if (cls === "awaiting" && sentAt) {
      return "Invite sent " + Moment(sentAt).format("MMM D, YYYY · h:mm A");
    }
    if (cls === "awaiting") return "Awaiting setup";
    return "No invite sent yet";
  }

  cadenceValue(feeKey) {
    return feeKey === "ffe"
      ? this.state.ffe_payout_cadence
      : this.state.intro_payout_cadence;
  }

  connectedCount() {
    return FEE_TYPES.filter((fee) => this.statusClass(fee.key) === "connected")
      .length;
  }

  render() {
    const isViewMode = !!this.state.slug;
    const connected = this.connectedCount();
    const total = FEE_TYPES.length;
    const progressPct = total > 0 ? Math.round((connected / total) * 100) : 0;
    // Same react-select style override CreditorStripeSetup.jsx uses, so the
    // cadence dropdown looks identical wherever it's edited.
    const customStyles = {
      option: (provided, state) => ({
        ...provided,
        color:
          state.isSelected && state.isFocused
            ? "white"
            : state.isSelected
              ? "black"
              : state.isFocused && "white",
        backgroundColor:
          state.isSelected && state.isFocused
            ? "black"
            : state.isFocused && "black",
      }),
      // .wh-fee-table has overflow: hidden to keep row borders clean, which
      // clips this menu unless it renders in a portal (see menuPortalTarget below).
      menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    };

    return (
      <main>
        <Container className="innter-container wh-creditor-detail">
          <div className="d-flex flex-column">
            <div className="row row-xs mg-t-0 pd-t-0 pd-lg-l-15 pd-xs-l-0">
              <div className="col-md-12">
                <div className="col-md-12 mg-t-20 mg-b-10"></div>
                <div className="col-md-12 mg-t-0 mg-b-10 pd-t-0 pd-b-10 pd-l-15 pd-r-0">
                  <div className="dashbaord-pageHeadertitle d-flex justify-content-between align-items-center flex-wrap mg-b-0">
                    <h3>Creditors</h3>
                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn-success-outline-small"
                        onClick={this.cancelClick}
                      >
                        Cancel
                      </button>
                      {global.userPermissions.checkPermission(
                        isViewMode ? "creditors-update" : "creditors-add",
                      ) && (
                        <button
                          className="btn-success ml-3"
                          type="button"
                          onClick={
                            isViewMode
                              ? this.updateCreditor
                              : this.saveCreditor
                          }
                          disabled={this.state.isSubmit}
                        >
                          {this.state.isSubmit ? global.loader : "Save"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="scrolling-carousel pd-l-15">
                  <div className="dashboardRightcard background-none pt-0 pl-4 pd-r-15">
                    <div className="col-md-12 pd-l-0 pd-lg-r-30 pd-xs-l-15 pd-xs-r-0 mg-t-15">
                      <Alerts
                        show={this.state.showAlertModal}
                        type={this.state.alertModalType}
                        title={this.state.alertModalTitle}
                        message={this.state.alertModalMessage}
                      />
                    </div>

                    <div className="col-md-12 pd-lg-l-30 pd-lg-r-30 pd-xs-l-15 pd-xs-r-0">
                      <div className="row mg-xs-t-0 mg-sm-t-0 mg-md-t-0 mg-lg-t-0 mg-xl-t-0 border-0">
                        <div className={"col-md-8 pd-xs-l-0 pd-xs-r-0"}>
                          <div className="mg-b-15">
                            <label className="form-label text-left pd-b-5">
                              Name
                            </label>
                            <Form.Control
                              onChange={this.changeHandler}
                              name="name"
                              value={this.state.name}
                              type="text"
                              maxLength="100"
                              autoComplete="off"
                            />
                            {this.state.errors.name && (
                              <div className="text-danger">
                                {this.state.errors.name}
                              </div>
                            )}
                          </div>

                          <div className="mg-b-15">
                            <label className="form-label text-left pd-b-5">
                              Email
                            </label>
                            <Form.Control
                              onChange={this.changeHandler}
                              name="email"
                              value={this.state.email}
                              type="text"
                              maxLength="100"
                              autoComplete="off"
                            />
                            {this.state.errors.email && (
                              <div className="text-danger">
                                {this.state.errors.email}
                              </div>
                            )}
                          </div>

                          <div className="mg-b-15">
                            <label className="form-label text-left pd-b-5">
                              Phone
                            </label>
                            <PhoneInput
                              country={global.country}
                              disableSearchIcon
                              enableSearch="true"
                              countryCodeEditable="true"
                              value={this.state.phone}
                              onChange={(phone) => this.setState({ phone })}
                            />
                            {this.state.errors.phone && (
                              <div className="text-danger">
                                {this.state.errors.phone}
                              </div>
                            )}
                          </div>
                          <div className="d-flex align-items-center justify-content-between mg-b-15">
                            <label className="form-label text-left mg-b-0">
                              Status
                            </label>
                            <div className="d-flex align-items-center">
                              <b
                                className={
                                  this.state.status_id ? "active" : "inactive"
                                }
                              ></b>
                              <div
                                className={
                                  this.state.status_id
                                    ? "az-toggle on"
                                    : "az-toggle"
                                }
                                onClick={() =>
                                  this.setState({
                                    status_id: !this.state.status_id,
                                    confirm_back: 1,
                                  })
                                }
                              >
                                <span></span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {isViewMode && (
                          <div className="col-md-4 pd-l-15 pd-r-15">
                            <label className="form-label text-left pd-b-5">
                              Payout connections
                            </label>
                            <div className="wh-rail-number">
                              <span className="wh-rail-n">{connected}</span>
                              <span className="wh-rail-of">
                                of {total} fees connected
                              </span>
                            </div>
                            <div className="wh-progress-track">
                              <span
                                className="wh-progress-fill"
                                style={{ width: progressPct + "%" }}
                              ></span>
                            </div>
                            <p className="wh-rail-caption">
                              Payouts start once the creditor completes
                              onboarding for a fee.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {isViewMode && (
                  <div className="scrolling-carousel pd-l-15 mg-t-20">
                    <div className="dashboardRightcard background-none pt-0 pl-4 pd-r-15">
                      <div className="col-md-12 pd-l-15 pd-r-15 mg-t-15">
                        <label className="form-label text-left pd-b-5">
                          Stripe Onboarding
                          <span className="wh-badge">
                            {connected}/{total} CONNECTED
                          </span>
                        </label>
                        <p>
                          Each fee has its own Stripe setup, even if they end up
                          pointing at the same bank account. Sending an invite
                          emails the creditor their onboarding link for that
                          fee.
                        </p>

                        <div className="wh-fee-table">
                          <div className="wh-fee-row-head">
                            <div>Fee</div>
                            <div>Connection status</div>
                            <div>Cadence</div>
                            <div className=" max-w200 ">Action</div>
                          </div>
                          {FEE_TYPES.map((fee) => {
                            let cls = this.statusClass(fee.key);
                            let btnLabel =
                              cls === "connected"
                                ? "Manage"
                                : cls === "awaiting"
                                  ? "Resend Invite"
                                  : "Send Invite";

                            return (
                              <div className="wh-fee-row" key={fee.key}>
                                <div>
                                  <div className="wh-fee-name">{fee.label}</div>
                                  <div className="wh-fee-meta">
                                    {this.metaText(fee.key)}
                                  </div>
                                </div>
                                <div>
                                  <span className={"wh-status-dot is-" + cls}>
                                    <span className="dot"></span>
                                    {this.statusLabel(fee.key)}
                                  </span>
                                </div>
                                <div className="max-w150">
                                  {cls === "connected" ? (
                                    <Select
                                      styles={customStyles}
                                      menuPortalTarget={document.body}
                                      isClearable={false}
                                      isDisabled={
                                        this.state.isCadenceSaving ||
                                        !global.userPermissions.checkPermission(
                                          "creditor-stripe-setup-update",
                                        )
                                      }
                                      className="multiselect"
                                      menuPlacement="auto"
                                      value={CADENCE_OPTIONS.find(
                                        (o) =>
                                          o.value === this.cadenceValue(fee.key),
                                      )}
                                      options={CADENCE_OPTIONS}
                                      onChange={(option) =>
                                        this.changeCadence(fee.key, option.value)
                                      }
                                      theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 2,
                                        colors: {
                                          ...theme.colors,
                                          primary: "#fff",
                                          primary75: "#000",
                                          primary50: "#000",
                                          primary25: "#000",
                                        },
                                      })}
                                    />
                                  ) : (
                                    <span className="wh-fee-meta">
                                      Set after Stripe setup
                                    </span>
                                  )}
                                </div>
                                <div className="wh-fee-action  max-w200  d-flex">
                                  {global.userPermissions.checkPermission(
                                    "creditor-stripe-setup-add",
                                  ) && (
                                    <button
                                      type="button"
                                      className="btn-success-outline-small"
                                      onClick={() =>
                                        this.sendStripeInvite(fee.key)
                                      }
                                      disabled={this.state.isSend}
                                    >
                                      {this.state.isSend &&
                                      this.state.sendingFeeKey === fee.key
                                        ? global.loader
                                        : btnLabel}
                                    </button>
                                  )}
                                  {/* Admin/Concierge/PM only — never shown to a Creditor
                                      viewing their own record. Lets them start Stripe
                                      onboarding on the creditor's behalf without sending
                                      the invite email at all. */}
                                  {cls !== "connected" &&
                                    global.userPermissions.checkPermission(
                                      "creditor-stripe-setup-add",
                                    ) &&
                                    global.currerntRoleSlug !== "creditor" && (
                                      <button
                                        type="button"
                                        className="btn-success-outline-small ml-2"
                                        onClick={() =>
                                          this.sendStripeSetupDirect(fee.key)
                                        }
                                        disabled={this.state.isSend}
                                      >
                                        {this.state.isSend &&
                                        this.state.sendingFeeKey ===
                                          fee.key + "_direct"
                                          ? global.loader
                                          : "Setup Directly"}
                                      </button>
                                    )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </main>
    );
  }
}

export default CreditorForm;
